/**
 * Zoea editor-canvas protocol contract (Platform <-> Website Module iframe).
 *
 * THIS FILE IS DUPLICATED BYTE-FOR-BYTE in both repositories:
 *   - zoea-platform:       frontend/src/modules/sites/editorProtocolContract.ts
 *   - zoea-website-module: apps/public-site/app/editor-canvas/editorProtocolContract.ts
 * It must stay dependency-free. Each repo pins the contract digest in its own
 * contract test (EDITOR_PROTOCOL_CONTRACT_DIGEST), so changing the protocol in
 * one repository without the other fails tests on at least one side.
 *
 * Platform is authoritative for authoring state; the canvas only *proposes*
 * edits. Every message carries the envelope fields below.
 */
export const EDITOR_PROTOCOL_ID = "zoea.editor-canvas";
export const EDITOR_PROTOCOL_VERSION = 1;

export type FieldKind =
  | "string" // non-empty, bounded
  | "text" // any string (may be empty), bounded
  | "int" // finite non-negative integer
  | "object" // plain object (deep-validated elsewhere)
  | "viewport" // { name, width, height }
  | "selection" // { kind, id? }
  | "chrome" // 'header' | 'footer'
  | "position" // { sectionId, columnId, index }
  | "placement" // partial GridPlacement, finite/non-negative-checked
  | "gridViewport"; // 'desktop' | 'tablet' | 'mobile'

export type Direction = "platform-to-canvas" | "canvas-to-platform";

export interface MessageSpec {
  direction: Direction;
  required: Record<string, FieldKind>;
}

/** Fields present on every message in addition to the per-message ones. */
export const ENVELOPE_FIELDS: Record<string, FieldKind> = {
  type: "string",
  protocolVersion: "int",
  nonce: "string",
  session: "string",
};

const P = "platform-to-canvas" as const;
const C = "canvas-to-platform" as const;

export const EDITOR_MESSAGES: Record<string, MessageSpec> = {
  "zoea:editor-canvas:render": {
    direction: P,
    required: {
      renderId: "int",
      pageId: "string",
      snapshot: "object",
      assetBaseUrl: "string",
      viewport: "viewport",
      selection: "selection",
    },
  },
  "zoea:editor-canvas:selection": { direction: P, required: { selection: "selection" } },
  "zoea:editor-canvas:ready": { direction: C, required: {} },
  "zoea:editor-canvas:rendered": { direction: C, required: { renderId: "int" } },
  "zoea:editor-canvas:render-error": {
    direction: C,
    required: { renderId: "int", message: "text" },
  },
  "zoea:editor-canvas:height": { direction: C, required: { height: "int" } },
  "zoea:editor-canvas:select-block": { direction: C, required: { blockId: "string" } },
  "zoea:editor-canvas:select-section": { direction: C, required: { sectionId: "string" } },
  "zoea:editor-canvas:select-chrome": { direction: C, required: { chrome: "chrome" } },
  "zoea:editor-canvas:navigation": { direction: C, required: { href: "text" } },
  "zoea:editor-canvas:edit-text": {
    direction: C,
    required: { renderId: "int", pageId: "string", blockId: "string", text: "text" },
  },
  "zoea:editor-canvas:move-block": {
    direction: C,
    required: { renderId: "int", pageId: "string", blockId: "string", target: "position" },
  },
  "zoea:editor-canvas:drag-block": {
    direction: C,
    required: {
      renderId: "int",
      pageId: "string",
      blockId: "string",
      viewport: "gridViewport",
      placement: "placement",
    },
  },
  "zoea:editor-canvas:resize-block": {
    direction: C,
    required: {
      renderId: "int",
      pageId: "string",
      blockId: "string",
      viewport: "gridViewport",
      placement: "placement",
    },
  },
};

export const SEMANTIC_VIEWPORTS = ["desktop", "tablet", "mobile"] as const;
export const SELECTION_KINDS = ["none", "block", "section", "header", "footer"] as const;
export const CHROME_KINDS = ["header", "footer"] as const;
/** Placement fields a drag may carry (position) vs a resize (size). */
export const DRAG_PLACEMENT_FIELDS = ["gridStart", "offsetX", "offsetY", "layer"] as const;
export const RESIZE_PLACEMENT_FIELDS = [
  "gridStart",
  "gridSpan",
  "offsetX",
  "offsetY",
  "width",
  "height",
] as const;
export const LAYERS = ["background", "normal", "foreground"] as const;
/** Grid tracks per semantic viewport; must equal Platform's grid.ts GRID_COLUMNS. */
export const GRID_COLUMNS = { desktop: 12, tablet: 8, mobile: 4 } as const;

export const MAX_ID_LENGTH = 128;
export const MAX_TEXT_LENGTH = 100_000;

/** Deterministic, key-sorted serialisation used for the cross-repo digest. */
export function contractFingerprintSource(): string {
  const sort = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(sort);
    if (value && typeof value === "object") {
      return Object.fromEntries(
        Object.entries(value as Record<string, unknown>)
          .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
          .map(([k, v]) => [k, sort(v)]),
      );
    }
    return value;
  };
  return JSON.stringify(
    sort({
      id: EDITOR_PROTOCOL_ID,
      version: EDITOR_PROTOCOL_VERSION,
      envelope: ENVELOPE_FIELDS,
      messages: EDITOR_MESSAGES,
      viewports: SEMANTIC_VIEWPORTS,
      selectionKinds: SELECTION_KINDS,
      chrome: CHROME_KINDS,
      dragFields: DRAG_PLACEMENT_FIELDS,
      resizeFields: RESIZE_PLACEMENT_FIELDS,
      layers: LAYERS,
      gridColumns: GRID_COLUMNS,
      samples: EDITOR_MESSAGE_SAMPLES,
    }),
  );
}

/**
 * One canonical, valid example per message (envelope excluded). Both
 * repositories' tests feed these to their own validator, so a payload-shape
 * change on either side breaks a test.
 */
export const EDITOR_MESSAGE_SAMPLES: Record<string, Record<string, unknown>> = {
  "zoea:editor-canvas:render": {
    renderId: 1,
    pageId: "page-1",
    snapshot: { pages: [] },
    assetBaseUrl: "https://media.example.com/base/",
    viewport: { name: "desktop", width: 1280, height: 900 },
    selection: { kind: "block", id: "block-1" },
  },
  "zoea:editor-canvas:selection": { selection: { kind: "footer" } },
  "zoea:editor-canvas:ready": {},
  "zoea:editor-canvas:rendered": { renderId: 1 },
  "zoea:editor-canvas:render-error": { renderId: 1, message: "boom" },
  "zoea:editor-canvas:height": { height: 900 },
  "zoea:editor-canvas:select-block": { blockId: "block-1" },
  "zoea:editor-canvas:select-section": { sectionId: "section-1" },
  "zoea:editor-canvas:select-chrome": { chrome: "header" },
  "zoea:editor-canvas:navigation": { href: "/about" },
  "zoea:editor-canvas:edit-text": {
    renderId: 1,
    pageId: "page-1",
    blockId: "block-1",
    text: "Hallo",
  },
  "zoea:editor-canvas:move-block": {
    renderId: 1,
    pageId: "page-1",
    blockId: "block-1",
    target: { sectionId: "section-1", columnId: "column-1", index: 0 },
  },
  "zoea:editor-canvas:drag-block": {
    renderId: 1,
    pageId: "page-1",
    blockId: "block-1",
    viewport: "desktop",
    placement: { gridStart: 2, offsetX: 8, offsetY: -8 },
  },
  "zoea:editor-canvas:resize-block": {
    renderId: 1,
    pageId: "page-1",
    blockId: "block-1",
    viewport: "desktop",
    placement: { gridSpan: 6, width: 320, height: 180 },
  },
};
